import { supabase } from './supabase';

function isRlsInsertDenied(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  return error.code === '42501' || msg.includes('row-level security policy');
}

interface CourseSelectionData {
  conversationId?: string | null;
  userId?: string | null;
  sessionId?: string | null;
  courseId?: string | null;
  courseName: string;
  mentorName?: string | null;
  courseType?: string | null;
  topic?: string | null;
  subTopic?: string | null;
  position?: number | null;
  totalCoursesShown?: number | null;
}

interface PopupViewData {
  conversationId?: string | null;
  userId?: string | null;
  sessionId?: string | null;
  coursesCount: number;
  topic?: string | null;
  subTopic?: string | null;
}

interface SearchAnalyticsData {
  conversationId?: string | null;
  userId?: string | null;
  sessionId?: string | null;
  topic?: string | null;
  subTopic?: string | null;
  learningType?: string | null;
  budget?: string | null;
  location?: string | null;
  coursesFound?: number;
  searchDurationMs?: number | null;
}

/**
 * Track when a user selects/clicks on a course from the recommendations
 */
export async function trackCourseSelection(data: CourseSelectionData): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from('course_selections').insert({
      conversation_id: data.conversationId,
      user_id: data.userId,
      session_id: data.sessionId,
      course_id: data.courseId,
      course_name: data.courseName,
      mentor_name: data.mentorName,
      course_type: data.courseType,
      topic: data.topic,
      sub_topic: data.subTopic,
      position: data.position,
      total_courses_shown: data.totalCoursesShown,
    });

    if (error) {
      if (isRlsInsertDenied(error)) {
        console.warn('[courseAnalyticsService] Skipping course selection analytics due to RLS policy.');
        return { success: true };
      }
      console.error('[courseAnalyticsService] Error tracking course selection:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('[courseAnalyticsService] Exception tracking course selection:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Track when the courses popup is opened/viewed
 */
export async function trackPopupView(data: PopupViewData): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from('course_popup_views').insert({
      conversation_id: data.conversationId,
      user_id: data.userId,
      session_id: data.sessionId,
      courses_count: data.coursesCount,
      topic: data.topic,
      sub_topic: data.subTopic,
    });

    if (error) {
      if (isRlsInsertDenied(error)) {
        console.warn('[courseAnalyticsService] Skipping popup analytics due to RLS policy.');
        return { success: true };
      }
      console.error('[courseAnalyticsService] Error tracking popup view:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('[courseAnalyticsService] Exception tracking popup view:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Track search behavior and results
 */
export async function trackSearch(data: SearchAnalyticsData): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.from('search_analytics').insert({
      conversation_id: data.conversationId,
      user_id: data.userId,
      session_id: data.sessionId,
      topic: data.topic,
      sub_topic: data.subTopic,
      learning_type: data.learningType,
      budget: data.budget,
      location: data.location,
      courses_found: data.coursesFound || 0,
      search_duration_ms: data.searchDurationMs,
    });

    if (error) {
      if (isRlsInsertDenied(error)) {
        console.warn('[courseAnalyticsService] Skipping search analytics due to RLS policy.');
        return { success: true };
      }
      console.error('[courseAnalyticsService] Error tracking search:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('[courseAnalyticsService] Exception tracking search:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Get current user ID if authenticated
 */
export async function getCurrentUserId(): Promise<string | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
}

/**
 * Generate a session ID for anonymous tracking
 * Uses a simple random string that persists in memory during the session
 */
let sessionId: string | null = null;

export function getOrCreateSessionId(): string {
  if (!sessionId) {
    sessionId = `session_${crypto.randomUUID()}`;
  }
  return sessionId;
}
